import { Box } from '@mui/material';
import { SharedRoute } from './features/sharing/SharedViews';
import { AccountAlerts } from './features/alerts/AccountAlerts';
import { useBillingSummary } from './features/billing/BillingSummaryProvider';
import { AlertsBadge } from './features/alerts/AlertsBadge';
import { useAlertsAccount } from './features/alerts/AlertsProvider';
import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useCollectionBrowseScope } from './features/collection/useCollectionBrowseScope';
import { useCollectionItemImages } from './features/images/useCollectionItemImages';
import { MultiCollectionWorkspace } from './features/collection/MultiCollectionWorkspace';
import Login from './components/Login';
import { EmailVerification } from './components/EmailVerification';
import { OnboardingUsernameScreen } from './components/OnboardingUsernameScreen';
import { AuthenticationError } from './components/AuthenticationError';
import 'react-image-crop/dist/ReactCrop.css';
import { useFeedback } from './features/feedback/FeedbackProvider';
import { logger } from './services/logger';
import { authService } from './services/authService';
import { authErrorMessage } from './services/authErrors';
import { useAppData } from './features/app/useAppData';
import { collectionCapabilities } from './features/collection/collectionCapabilities';
import { usePasswordChange } from './features/app/usePasswordChange';
import {
  useNavigation,
  useNavigationState,
} from './features/navigation/NavigationProvider';
import {
  appDestination,
  parentDestination,
  parseAppRoute,
} from './features/app/appRoutes';
import { BrandLogo } from './components/ui/BrandLogo';
import { AppShell } from './components/ui/AppShell';
import { AppLoadingScreen } from './components/ui/AppLoadingScreen';
import { useUiPreferences } from './hooks/useUiPreferences';
import { usePhoneLayout } from './hooks/usePhoneLayout';
import { AppTab } from './components/ui/navigation';
import { DesktopNavigation } from './components/ui/DesktopNavigation';
import { MobileFooter } from './components/ui/MobileFooter';
import { useBinActions } from './features/items/useBinActions';
import { CollectionTopBar } from './components/ui/CollectionTopBar';
import { MoreDestination } from './components/ui/MoreSheet';
import {
  AppChildWorkflow,
  AppSurface,
  AppWorkflowSurfaces,
} from './features/app/AppWorkflowSurfaces';
import { CollectionSessionGate } from './features/collection/components/CollectionSessionGate';
import { useCollectionCapacityCapability } from './features/capacity/useCollectionCapacityCapability';
import { createCollectionManagementController } from './features/collection/collectionManagementService';
import { useInvitationLanding } from './features/collection/useInvitationLanding';
import { GpsCoordinates } from './domain/location';
import {
  CaptureQueueBadge,
  CaptureQueueEntry,
  RegisterCaptureQueue,
} from './features/rapidCapture/CaptureQueueBadge';

type AppPresentationChild = AppChildWorkflow | 'collection-create';
function CollectionApp() {
  const { notify } = useFeedback();
  const navigation = useNavigation();
  const route = parseAppRoute(navigation.location.pathname);
  const activeTab = route.tab;
  const surface = route.surface;
  const [menuOpen, setMenuOpen] = useNavigationState('app.more', false);
  const [childWorkflow, setChildWorkflow] =
    useNavigationState<AppPresentationChild | null>('app.child', null);
  const setActiveTab = (tab: AppTab) =>
    navigation.navigate(appDestination(tab));
  const [purchaseReturn] = useState<'returned' | 'cancelled' | undefined>(
    () => {
      const value = new URLSearchParams(navigation.location.search).get(
        'creditPurchase',
      );
      return value === 'returned' || value === 'cancelled' ? value : undefined;
    },
  );
  const openedPurchaseReturn = useRef(false);
  const [captureQueues, setCaptureQueues] = useState<CaptureQueueEntry[]>([]);
  const registerCaptureQueue = useCallback<RegisterCaptureQueue>(
    (userId, collectionId, entry) => {
      setCaptureQueues((current) => {
        const remaining = current.filter(
          (queue) =>
            queue.userId !== userId || queue.collectionId !== collectionId,
        );
        if (!entry && remaining.length === current.length) return current;
        return entry ? [...remaining, entry] : remaining;
      });
    },
    [],
  );
  const moreButtonRef = useRef<HTMLButtonElement>(null);
  const menuTriggerRef = useRef<HTMLButtonElement | null>(null);
  const uiPreferences = useUiPreferences();
  const { preferences, setCardSize, setRemoveMargins } = uiPreferences;
  const phoneLayout = usePhoneLayout();
  const {
    user,
    authStatus,
    authError,
    authNotice,
    authInitialized,
    deletedItems,
    collectionStatus = 'ready',
    collectionSession,
    activeCollectionId,
    activeCollectionRole,
    activeMembershipVersion,
    inventoryAuthority,
    compatibilityMode,
    retryCollectionSession,
    captureCollectionSessionContext,
    retryCollectionSessionIfCurrent,
    loadMoreCollections,
    switchCollection,
    updateLastUsedLocation,
    updateUsername,
    refreshVerification,
    resendVerification,
    verificationDeliveryFailed,
    retryProvisioning,
    refreshSession,
    signOut,
  } = useAppData(surface === 'bin', false);
  useAlertsAccount(authStatus === 'ready' ? user?.uid : undefined);
  useBillingSummary(authStatus === 'ready' ? user?.uid : undefined, true);
  const browse = useCollectionBrowseScope(
    user?.uid,
    collectionSession,
    loadMoreCollections,
  );
  const multiBrowse = compatibilityMode === 'collection-only';
  const invitationLanding = useInvitationLanding({
    authenticated: authStatus === 'ready',
    onAccepted: refreshSession,
  });
  const collectionManagement = useMemo(() => {
    const controller = createCollectionManagementController();
    if (!user?.uid) controller.reset();
    return controller;
  }, [user?.uid]);
  const activeCollectionSummary =
    collectionSession?.availableCollections.items.find(
      ({ collectionId }) => collectionId === activeCollectionId,
    );
  const activeCollectionLifecycle =
    activeCollectionSummary?.lifecycle ?? 'active';
  const binVisible = surface === 'bin';
  const binItems = useCollectionItemImages(
    user?.uid ?? '',
    binVisible ? deletedItems : [],
    binVisible &&
      authStatus === 'ready' &&
      collectionStatus === 'ready' &&
      activeCollectionSummary
      ? [activeCollectionSummary]
      : [],
    binVisible ? deletedItems : null,
  );
  const {
    canEditInventory: roleCanEditInventory,
    canManageCollection,
    canAdministerInventory: roleCanAdministerInventory,
    canManageMembers,
    canRenameCollection: roleCanRenameCollection,
    canViewActivity,
  } = collectionCapabilities(
    compatibilityMode,
    activeCollectionRole,
    activeCollectionLifecycle,
  );
  const capacity = useCollectionCapacityCapability({
    userId: user?.uid,
    enabled:
      authStatus === 'ready' &&
      collectionStatus === 'ready' &&
      (!multiBrowse || browse.single?.collectionId === activeCollectionId),
    collectionId:
      compatibilityMode === 'collection-only' &&
      activeCollectionLifecycle !== 'deleted'
        ? activeCollectionId
        : undefined,
    membershipVersion: activeMembershipVersion,
    role: activeCollectionRole,
  });
  const billingAccess = capacity.displayState ?? capacity.state;
  const billingRestricted =
    billingAccess.status === 'ready' &&
    ['restricted', 'deleting'].includes(billingAccess.value.access);
  const canEditInventory = roleCanEditInventory && !billingRestricted;
  const canAdministerInventory = roleCanAdministerInventory;
  const canRenameCollection = roleCanRenameCollection && !billingRestricted;
  const mutationAuthority = canEditInventory ? inventoryAuthority : undefined;
  const ownerAuthority = canManageCollection ? inventoryAuthority : undefined;
  const inventoryAdminAuthority = canAdministerInventory
    ? inventoryAuthority
    : undefined;
  const invalidateCapacity = capacity.invalidate;
  const readyUser =
    authStatus === 'ready' && collectionStatus === 'ready' ? user : null;
  const handlePasswordChange = usePasswordChange(readyUser);

  const binActions = useBinActions({
    authority: mutationAuthority,
    permanentDeleteAuthority: inventoryAdminAuthority,
    closeBin: () => navigation.goBack(appDestination(activeTab)),
    setLastUsedLocation: updateLastUsedLocation,
  });

  const handleCollectionSwitch = async (collectionId: string) => {
    if (collectionId === activeCollectionId) return;
    await navigation.requestAction(async () => {
      navigation.reset();
      navigation.navigate(appDestination(activeTab), { replace: true });
      await switchCollection(collectionId);
    });
  };
  const handleCreateCollection = async (name: string) => {
    const context = captureCollectionSessionContext();
    if (!context) throw new Error('The collection session is unavailable.');
    const result = await collectionManagement.create(name);
    const refreshed = await retryCollectionSessionIfCurrent(context);
    if (!refreshed) return;
    if (
      refreshed.status !== 'ready' ||
      refreshed.session.activeCollectionId !== result.collectionId
    ) {
      throw new Error('The created collection could not be refreshed.');
    }
    collectionManagement.acknowledgeCreate();
  };
  const handleRenameCollection = async (name: string) => {
    if (!activeCollectionId || !canRenameCollection) {
      throw new Error(
        'Collection management access is required for this change.',
      );
    }
    const context = captureCollectionSessionContext();
    if (!context) throw new Error('The collection session is unavailable.');
    const result = await collectionManagement.update(activeCollectionId, {
      name,
    });
    const refreshed = await retryCollectionSessionIfCurrent(context);
    if (!refreshed) return;
    if (
      refreshed.status !== 'ready' ||
      refreshed.session.activeCollectionId !== result.collectionId
    ) {
      throw new Error('The renamed collection could not be refreshed.');
    }
    collectionManagement.acknowledgeUpdate();
  };
  const handleUpdateCollectionGps = async (
    collectionId: string,
    gps: GpsCoordinates | null,
  ) => {
    const target = collectionSession?.availableCollections.items.find(
      ({ collectionId: candidate }) => candidate === collectionId,
    );
    if (
      !target ||
      target.role !== 'owner' ||
      target.lifecycle !== 'active' ||
      billingRestricted
    ) {
      throw new Error('Owner access is required for this collection change.');
    }
    const context = captureCollectionSessionContext();
    if (!context) throw new Error('The collection session is unavailable.');
    await collectionManagement.update(collectionId, { gps });
    const refreshed = await retryCollectionSessionIfCurrent(context);
    if (!refreshed || refreshed.status !== 'ready') {
      throw new Error('The collection GPS location could not be refreshed.');
    }
    collectionManagement.acknowledgeUpdate();
  };
  const collectionManagementReady = compatibilityMode === 'collection-only';
  // A URL selects a collection, but only the verified session supplies authority.
  // No asynchronous completion opens a destination: the current route owns it.
  const requestedSession = useRef<string | undefined>(undefined);
  const routeCollection = route.collectionId;
  const routeCollectionKnown =
    !routeCollection ||
    Boolean(
      collectionSession?.availableCollections.items.some(
        (entry) =>
          entry.collectionId === routeCollection &&
          entry.lifecycle !== 'deleted',
      ),
    );
  const routeSessionReady =
    !routeCollection ||
    (routeCollectionKnown &&
      activeCollectionId === routeCollection &&
      collectionStatus === 'ready');
  useEffect(() => {
    if (!route.valid)
      navigation.navigate(appDestination(activeTab), { replace: true });
  }, [route.valid, activeTab, navigation]);
  useEffect(() => {
    if (
      !user ||
      authStatus !== 'ready' ||
      !routeCollection ||
      !routeCollectionKnown ||
      routeSessionReady ||
      collectionStatus === 'loading' ||
      collectionStatus === 'idle'
    )
      return;
    const request = `${user.uid}:${routeCollection}:${navigation.location.key}`;
    if (requestedSession.current === request) return;
    requestedSession.current = request;
    void switchCollection(routeCollection).catch(() =>
      notify('Unable to load this collection.', 'error'),
    );
  }, [
    user,
    authStatus,
    routeCollection,
    routeCollectionKnown,
    routeSessionReady,
    collectionStatus,
    switchCollection,
    navigation.location.key,
    notify,
  ]);
  const previousUser = useRef(user?.uid);
  useEffect(() => {
    if (previousUser.current && previousUser.current !== user?.uid)
      navigation.reset();
    previousUser.current = user?.uid;
  }, [user?.uid, navigation]);
  const handleSignOut = async () => {
    try {
      await navigation.requestAction(async () => {
        await signOut();
        navigation.reset();
        navigation.navigate('/items', { replace: true });
      });
    } catch (error) {
      logger.error('Error signing out:', error);
      notify('Unable to sign out. Please try again.', 'error');
    }
  };

  const handleConnectGoogle = async () => {
    try {
      await authService.linkGoogle();
      await refreshSession();
    } catch (error) {
      logger.error('Error connecting Google account:', error);
      notify(authErrorMessage(error), 'error');
    }
  };

  useEffect(() => {
    if (
      !purchaseReturn ||
      openedPurchaseReturn.current ||
      authStatus !== 'ready' ||
      collectionStatus !== 'ready' ||
      !user?.username
    )
      return;
    openedPurchaseReturn.current = true;
    navigation.navigate(appDestination(activeTab, 'billing'), {
      replace: true,
    });
  }, [
    purchaseReturn,
    authStatus,
    collectionStatus,
    user?.username,
    navigation,
    activeTab,
  ]);

  if (!authInitialized || authStatus === 'provisioning') {
    return <AppLoadingScreen />;
  }
  if (authStatus === 'signed-out' || !user) {
    return <Login sessionError={authError} sessionNotice={authNotice} />;
  }
  if (authStatus === 'verification-required') {
    return (
      <EmailVerification
        email={user.email}
        onRefresh={refreshVerification}
        onResend={resendVerification}
        deliveryFailed={verificationDeliveryFailed}
        onSignOut={signOut}
      />
    );
  }
  if (authStatus === 'error') {
    return (
      <AuthenticationError
        message={
          authError || 'qkIndex could not finish setting up this account.'
        }
        onRetry={retryProvisioning}
        onSignOut={signOut}
      />
    );
  }
  if (authStatus !== 'ready') {
    return <AppLoadingScreen />;
  }

  if (!user.username) {
    return (
      <OnboardingUsernameScreen
        userId={user.uid}
        onUsernameSet={updateUsername}
        onSignOut={signOut}
      />
    );
  }

  const openMore = (trigger = moreButtonRef.current) => {
    menuTriggerRef.current = trigger;
    setMenuOpen(true);
  };
  const closeMenu = () => {
    setMenuOpen(false);
    window.setTimeout(() => {
      const trigger = menuTriggerRef.current;
      (trigger?.isConnected ? trigger : moreButtonRef.current)?.focus();
    }, 0);
  };
  const openChildWorkflow = (child: AppPresentationChild) =>
    setChildWorkflow(child);
  const closeChildWorkflow = () => setChildWorkflow(null);
  const navigateSurface = (
    next: AppSurface,
    collectionId = route.collectionId ||
      browse.single?.collectionId ||
      activeCollectionId,
  ) => {
    navigation.navigate(appDestination(activeTab, next, collectionId));
  };
  const backSurface = () => {
    if (childWorkflow) closeChildWorkflow();
    else
      navigation.goBack(
        parentDestination(activeTab, surface, route.collectionId),
      );
  };
  const closeSurface = () => {
    if (surface === 'account')
      navigation.navigate(appDestination(activeTab), { replace: true });
    else backSurface();
  };
  const openCollectionSwitcher = () => navigateSurface('collections');
  const closeCollectionSwitcher = backSurface;
  const manageCollectionById = (
    collectionId: string,
    destination: 'collection' | 'transfer' | 'sharing' = 'collection',
  ) => {
    if (
      !collectionSession?.availableCollections.items.some(
        (entry) => entry.collectionId === collectionId,
      )
    )
      return;
    navigateSurface(destination, collectionId);
  };
  const handleMoreDestination = (destination: MoreDestination) => {
    if (
      multiBrowse &&
      !browse.single &&
      !['account', 'appearance', 'billing', 'shared-with-me'].includes(
        destination,
      )
    ) {
      openCollectionSwitcher();
      return;
    }
    navigateSurface(destination);
  };
  const collectionActionsAvailable =
    (!multiBrowse || Boolean(browse.single)) &&
    collectionManagementReady &&
    Boolean(activeCollectionId) &&
    Boolean(activeCollectionRole);
  const capacityDisplayState = capacity.displayState ?? capacity.state;
  const collectionCapacity =
    (!multiBrowse || browse.single?.collectionId === activeCollectionId) &&
    (activeCollectionRole === 'owner' ||
      activeCollectionRole === 'administrator' ||
      activeCollectionRole === 'editor')
      ? capacityDisplayState.status === 'ready'
        ? {
            status: 'ready' as const,
            countedItems: capacityDisplayState.value.countedItems,
            effectiveLimit: capacityDisplayState.value.effectiveLimit,
          }
        : { status: capacityDisplayState.status }
      : undefined;
  const collectionName = multiBrowse
    ? browse.label
    : activeCollectionSummary?.name ||
      collectionSession?.activeCollectionId ||
      'My collection';
  const displayedRole = multiBrowse
    ? browse.single?.role
    : activeCollectionRole;
  const captureQueueBadge = (
    <CaptureQueueBadge
      key={user?.uid}
      queues={captureQueues.filter((queue) => queue.userId === user?.uid)}
    />
  );
  const inventoryShellHeader = phoneLayout ? (
    <CollectionTopBar
      phoneLayout
      embedded
      role={displayedRole || undefined}
      username={user?.username || undefined}
      lifecycle={
        multiBrowse
          ? browse.single?.lifecycle || 'active'
          : activeCollectionLifecycle
      }
      capacity={collectionCapacity}
      onMore={() => openMore()}
      moreOpen={menuOpen}
      moreButtonRef={moreButtonRef}
      headerAction={
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {captureQueueBadge}
          <AlertsBadge key={`alerts:${user.uid}`} userId={user.uid} />
        </Box>
      }
    />
  ) : (
    <DesktopNavigation
      embedded
      value={activeTab}
      onChange={setActiveTab}
      onMore={() => openMore()}
      moreOpen={menuOpen}
      moreButtonRef={moreButtonRef}
      headerAction={
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {captureQueueBadge}
          <AlertsBadge key={`alerts:${user.uid}`} userId={user.uid} />
        </Box>
      }
      leading={<BrandLogo />}
      collection={{
        name: collectionName,
        role: displayedRole || undefined,
        capacity: collectionCapacity,
        onOpen: openCollectionSwitcher,
      }}
    />
  );

  return (
    <AppShell removeMargins={preferences.removeMargins} flushTop>
      <AccountAlerts
        userId={user.uid}
        purchaseReturn={purchaseReturn}
        openBilling={() => navigateSurface('billing')}
      />
      {invitationLanding}
      <AppWorkflowSurfaces
        sessionReady={collectionStatus === 'ready' && routeSessionReady}
        collectionTransition={
          route.collectionId && !routeSessionReady
            ? !routeCollectionKnown ||
              collectionStatus === 'error' ||
              collectionStatus === 'revoked'
              ? 'unavailable'
              : 'loading'
            : undefined
        }
        collectionSelector={{
          open: surface === 'collections',
          onClose: closeCollectionSwitcher,
          session: collectionSession,
          activeCollectionId,
          onSelect: (collectionId) => {
            void handleCollectionSwitch(collectionId);
          },
          onCreate: collectionManagementReady
            ? handleCreateCollection
            : undefined,
          onManage:
            collectionManagementReady &&
            collectionStatus !== 'loading' &&
            collectionStatus !== 'idle'
              ? manageCollectionById
              : undefined,
          onLoadMore: collectionSession?.availableCollections.nextCursor
            ? loadMoreCollections
            : undefined,
          browseScope: multiBrowse ? browse.scope : undefined,
          multiCollection: browse.multiCollection,
          onMultiCollectionChange: multiBrowse
            ? browse.setMultiCollection
            : undefined,
          onBrowseScopeChange: multiBrowse ? browse.setScope : undefined,
          nameDialog: childWorkflow === 'collection-create' ? 'create' : null,
          onNameDialogChange: (next) => {
            if (next === null) closeChildWorkflow();
            else openChildWorkflow('collection-create');
          },
        }}
        menu={{
          open: menuOpen,
          onClose: closeMenu,
          onSelect: handleMoreDestination,
          collectionName: collectionActionsAvailable
            ? collectionName
            : undefined,
        }}
        bin={{
          open: surface === 'bin',
          onClose: closeSurface,
          deletedItems: binItems,
          onRestore: binActions.restore,
          onRestoreItems: binActions.restoreItems,
          onDeleteItems: binActions.deleteItems,
          onEmptyBin: () => binActions.empty(deletedItems),
          readOnly: !canEditInventory,
          canRestore: canEditInventory,
          canEmptyBin: canAdministerInventory,
          collectionId: activeCollectionId,
          membershipVersion: activeMembershipVersion,
          currentUserId: user?.uid,
          currentUsername: user?.username || undefined,
        }}
        purchaseReturn={purchaseReturn}
        surface={surface}
        onClose={closeSurface}
        onBack={surface ? backSurface : undefined}
        onNavigate={navigateSurface}
        cardSize={preferences.cardSize}
        onCardSizeChange={setCardSize}
        removeMargins={preferences.removeMargins}
        onRemoveMarginsChange={setRemoveMargins}
        account={{
          changePassword: handlePasswordChange,
          connectGoogle: handleConnectGoogle,
          providerIds: user.providerIds,
          userId: user.uid,
          username: user.username || undefined,
          usernameUpdatedAt: user.usernameUpdatedAt,
          onUsernameChanged: updateUsername,
        }}
        onSignOut={handleSignOut}
        ownerAuthority={ownerAuthority}
        inventoryAdminAuthority={inventoryAdminAuthority}
        canRenameCollection={canRenameCollection}
        activityCollectionId={canViewActivity ? activeCollectionId : undefined}
        membershipVersion={activeMembershipVersion}
        inventoryAuthority={inventoryAuthority}
        readOnly={!canEditInventory}
        canManageCollection={canManageCollection}
        collectionActionsAvailable={collectionActionsAvailable}
        collectionName={
          activeCollectionSummary?.name ||
          collectionSession?.activeCollectionId ||
          'My collection'
        }
        collectionRole={activeCollectionRole || undefined}
        collectionLifecycle={activeCollectionLifecycle}
        collectionGps={activeCollectionSummary?.gps}
        onUpdateCollectionGps={
          collectionManagementReady &&
          !billingRestricted &&
          activeCollectionId &&
          activeCollectionRole === 'owner' &&
          activeCollectionLifecycle === 'active'
            ? (gps) => handleUpdateCollectionGps(activeCollectionId, gps)
            : undefined
        }
        onRenameCollection={
          collectionManagementReady && canRenameCollection
            ? handleRenameCollection
            : undefined
        }
        childWorkflow={
          childWorkflow === 'gps' ||
          childWorkflow === 'rename' ||
          childWorkflow === 'password' ||
          childWorkflow === 'username'
            ? childWorkflow
            : null
        }
        onOpenChild={openChildWorkflow}
        onCloseChild={closeChildWorkflow}
        peopleCollectionId={canManageMembers ? activeCollectionId : undefined}
        leaveCollection={
          activeCollectionId &&
          (activeCollectionRole === 'administrator' ||
            activeCollectionRole === 'editor' ||
            activeCollectionRole === 'viewer')
            ? {
                collectionId: activeCollectionId,
                userId: user.uid,
                role: activeCollectionRole,
                onLeft: refreshSession,
              }
            : undefined
        }
        transferCollection={
          activeCollectionId &&
          (activeCollectionRole === 'owner' ||
            activeCollectionRole === 'administrator' ||
            activeCollectionRole === 'editor')
            ? {
                collectionId: activeCollectionId,
                userId: user.uid,
                role: activeCollectionRole,
                onChanged: async () => {
                  await refreshSession();
                  invalidateCapacity();
                },
              }
            : undefined
        }
        deletionCollection={
          activeCollectionId &&
          activeCollectionRole === 'owner' &&
          activeCollectionSummary &&
          activeCollectionSummary.lifecycle === 'active'
            ? {
                collectionId: activeCollectionId,
                userId: user.uid,
                collectionName: activeCollectionSummary.name,
                lifecycle: activeCollectionSummary.lifecycle,
                onChanged: async () => {
                  closeSurface();
                  await refreshSession();
                  invalidateCapacity();
                },
              }
            : undefined
        }
      />
      <CollectionSessionGate
        status={collectionStatus}
        session={collectionSession}
        onRetry={() => {
          void retryCollectionSession();
        }}
        onSelect={(collectionId) => {
          void handleCollectionSwitch(collectionId);
        }}
        onLoadMore={
          collectionSession?.availableCollections.nextCursor
            ? loadMoreCollections
            : undefined
        }
        onRename={
          collectionManagementReady && canRenameCollection
            ? handleRenameCollection
            : undefined
        }
        onCreate={
          collectionManagementReady ? handleCreateCollection : undefined
        }
        hideCollectionControls
      >
        <MultiCollectionWorkspace
          key={user.uid}
          userId={user.uid}
          username={user.username || undefined}
          collections={[
            ...(collectionSession?.availableCollections.items || []),
          ]}
          collectionIds={browse.ids}
          activeTab={activeTab}
          phoneLayout={phoneLayout}
          shellHeader={inventoryShellHeader}
          ui={uiPreferences}
          renderMobileFooter={(action, counts) => (
            <MobileFooter
              action={action}
              itemCount={counts?.items}
              locationCount={counts?.locations}
              value={activeTab}
              onChange={setActiveTab}
              onOpenCollection={openCollectionSwitcher}
              collectionName={collectionName}
              collectionsOpen={surface === 'collections'}
            />
          )}
          discovering={browse.discovering}
          discoveryError={browse.discoveryError}
          onRetryDiscovery={browse.retryDiscovery}
          onViewCollection={(collectionId) => {
            browse.setScope({ kind: 'selected', ids: [collectionId] });
            setActiveTab('items');
          }}
          onOpenBilling={() => navigateSurface('billing')}
          onManageCollection={(collectionId) =>
            manageCollectionById(collectionId, 'transfer')
          }
          onManageTags={(collectionId) =>
            manageCollectionById(collectionId, 'sharing')
          }
          registerCaptureQueue={registerCaptureQueue}
        />
      </CollectionSessionGate>
    </AppShell>
  );
}

function App() {
  const navigation = useNavigation();
  return navigation.location.pathname.startsWith('/shared/') ? (
    <SharedRoute key={navigation.location.pathname} />
  ) : (
    <CollectionApp />
  );
}
export default App;
